import { Link as RouterLink, useRouter } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "react-aria-components/Button";
import { Disclosure, DisclosurePanel } from "react-aria-components/Disclosure";
import { cn } from "tailwind-variants";
import { useMediaQuery, useOnClickOutside } from "usehooks-ts";
import type { ApiBlockGridItemModel } from "#/_generated/umbraco/delivery-api/models";
import { BlockGridItems } from "#/_packages/umbrella/integrations/umbraco/property-components/block-grid.tsx";
import type { Link } from "#/_packages/umbrella/types";
import {
	displaySoftHyphens,
	stripSoftHyphens,
} from "#/_packages/umbrella/utils/soft-hyphens";
import { m } from "#/paraglide/messages";

export type MegaMenuContainerType = "link" | "text";
export type MegaMenuWidth = "narrow" | "normal" | "wide";

export type MegaMenuItem = {
	id: string;
	containerType: MegaMenuContainerType;
	label: string;
	link?: Link;
	isActive?: boolean;
	width?: MegaMenuWidth;
	noVerticalSpacing?: boolean;
	content?: ApiBlockGridItemModel[] | undefined;
};

export type MegaMenuProps = {
	id?: string;
	items: readonly MegaMenuItem[];
	hoverShow?: boolean;
	navLabel?: string;
	className?: string;
};

const widthClasses: Record<MegaMenuWidth, string> = {
	narrow: "nav-mega-menu-sub-narrow",
	normal: "nav-mega-menu-sub-normal",
	wide: "nav-mega-menu-sub-wide",
};

function resolveToggleLabel(item: MegaMenuItem) {
	return m.toggle_subpages({ label: stripSoftHyphens(item.label) });
}

function relForLink(link: Link) {
	const rel: string[] = [];
	if (link.target === "_blank") rel.push("noopener", "noreferrer");
	if (link.noFollow) rel.push("nofollow");
	return rel.length > 0 ? rel.join(" ") : undefined;
}

function MegaMenuLink({ item }: { item: MegaMenuItem }) {
	if (item.link && !item.link.isExternal) {
		return (
			<RouterLink
				aria-current={item.isActive ? "page" : undefined}
				className="nav-mega-menu-link btn"
				to={"/$"}
				params={{ _splat: item.link.href }}
				rel={relForLink(item.link)}
				target={item.link.target}
				data-tracking-event={item.link.trackingEvent}
			>
				<span className="nav-mega-menu-nav-label">
					{displaySoftHyphens(item.label)}
				</span>
			</RouterLink>
		);
	}

	if (item.link) {
		return (
			<a
				aria-current={item.isActive ? "page" : undefined}
				className="nav-mega-menu-link btn"
				href={item.link.href}
				rel={relForLink(item.link)}
				target={item.link.target}
				data-tracking-event={item.link.trackingEvent}
			>
				<span className="nav-mega-menu-nav-label">
					{displaySoftHyphens(item.label)}
				</span>
			</a>
		);
	}

	return (
		<span className="nav-mega-menu-nav-label">
			{displaySoftHyphens(item.label)}
		</span>
	);
}

function MegaMenuToggle({
	isExpanded,
	item,
	onPressStart,
}: {
	isExpanded: boolean;
	item: MegaMenuItem;
	onPressStart: (pointerType: string) => void;
}) {
	return (
		<Button
			onPressStart={(event) => onPressStart(event.pointerType)}
			aria-label={resolveToggleLabel(item)}
			className={cn(
				"nav-mega-menu-sub-nav-toggler appearance-none border-0 bg-transparent",
				isExpanded && "active",
			)}
			slot="trigger"
			type="button"
		>
			<ChevronDown
				aria-hidden="true"
				className={cn(
					"icon icon-20 icon-lg-16 item-icon size-5 lg:size-4",
					isExpanded ? "rotate-180" : "",
				)}
			/>
		</Button>
	);
}

function MegaMenuItemDisclosure({
	isExpanded,
	hoverShow,
	item,
	onExpandedChange,
}: {
	isExpanded: boolean;
	hoverShow: boolean;
	item: MegaMenuItem;
	onExpandedChange: (isExpanded: boolean) => void;
}) {
	//TODO ADD CONTEXT SO MENU CLOSES WHEN CLICKING ON LINK

	const ref = useRef<HTMLDivElement>(null);
	const openedByHover = useRef(false);
	useOnClickOutside<any>([ref], () => {
		openedByHover.current = false;
		onExpandedChange(false);
	});

	return (
		<Disclosure
			onClick={(e) => {
				const target = e.target as any;
				if (target.hasAttribute("data-submenu-link")) onExpandedChange(false);
			}}
			ref={ref}
			// React Aria owns visibility; CSS-only hover can expose a hidden panel.
			className="nav-item nav-mega-menu-item"
			isExpanded={isExpanded}
			onExpandedChange={(expanded) => {
				// Pointer entry runs before the first click. That click should keep
				// the menu open; a subsequent activation can close it normally.
				if (!expanded && openedByHover.current) {
					openedByHover.current = false;
					return;
				}
				openedByHover.current = false;
				onExpandedChange(expanded);
			}}
		>
			{/* biome-ignore lint/a11y/useSemanticElements: This groups navigation controls rather than form fields. */}
			<div
				role="group"
				aria-label={stripSoftHyphens(item.label)}
				onKeyDown={(event) => {
					if (event.key === "Escape") {
						openedByHover.current = false;
						onExpandedChange(false);
					}
				}}
				onPointerEnter={
					hoverShow
						? (event) => {
								if (event.pointerType === "mouse") {
									openedByHover.current = !isExpanded;
									onExpandedChange(true);
								}
							}
						: undefined
				}
				onPointerLeave={
					hoverShow
						? (event) => {
								if (event.pointerType === "mouse") {
									openedByHover.current = false;
									onExpandedChange(false);
								}
							}
						: undefined
				}
			>
				<div
					className={cn(
						"nav-mega-menu-link-wrapper justify-between",
						item.isActive && "active",
					)}
				>
					<MegaMenuLink item={item} />
					<MegaMenuToggle
						isExpanded={isExpanded}
						item={item}
						onPressStart={(pointerType) => {
							if (pointerType !== "mouse") openedByHover.current = false;
						}}
					/>
				</div>
				<DisclosurePanel
					className={cn(
						"nav-mega-menu-sub-wrapper",
						widthClasses[item.width ?? "narrow"],
						isExpanded && "expanded",
					)}
				>
					<div className="nav-mega-menu-sub">
						<div
							className={cn(
								item.width === "wide"
									? item.noVerticalSpacing && "no-row-gap"
									: !item.noVerticalSpacing && "flex flex-col gap-3",
							)}
						>
							<BlockGridItems items={item.content} />
						</div>
					</div>
				</DisclosurePanel>
			</div>
		</Disclosure>
	);
}

function MegaMenuStaticItem({ item }: { item: MegaMenuItem }) {
	return (
		<div className={cn("nav-item nav-mega-menu-item")}>
			<div
				className={cn("nav-mega-menu-link-wrapper", item.isActive && "active")}
			>
				<MegaMenuLink item={item} />
			</div>
		</div>
	);
}

export function MegaMenu({
	id,
	items,
	hoverShow = false,
	navLabel,
	className,
}: MegaMenuProps) {
	const isPreview = !!useRouter().options.context.previewBlock;

	const [expandedItemId, setExpandedItemId] = useState<string>();
	const isDesktop = useMediaQuery("(min-width: 1200px)");
	const enableHoverShow = hoverShow && isDesktop;
	if (items.length === 0) {
		return null;
	}

	return (
		<nav
			aria-label={navLabel ?? m.main_navigation()}
			className={cn("block__mega-menu nav-mega-menu", className)}
			id={id}
		>
			<div className="nav-divider xl:hidden" />
			<div className="nav">
				{items.map((item) =>
					item.content && !isPreview ? (
						<MegaMenuItemDisclosure
							hoverShow={enableHoverShow}
							isExpanded={expandedItemId === item.id}
							item={item}
							key={`nav-${item.id}`}
							onExpandedChange={(isExpanded) => {
								setExpandedItemId((currentItemId) => {
									if (isExpanded) {
										return item.id;
									}

									return currentItemId === item.id ? undefined : currentItemId;
								});
							}}
						/>
					) : (
						<MegaMenuStaticItem item={item} key={`nav-static-${item.id}`} />
					),
				)}
			</div>
			<div className="nav-divider mb-0 xl:hidden" />
		</nav>
	);
}
